import { BaseTextField } from '@components/BaseTextField';
import { BaseIconFrame, isClosedPolygon } from '@components/FramePolygonSized';
import { GeoZoneCoordinates, GeoZones, GeoZoneType } from '@EcamModel/model';
import { Skeleton, Stack, Typography } from '@mui/material';
import { getCenterFrame } from '@pages/cameras/details/utils';
import useFramePolygonGeoZones from '@pages/cameras/hook/useFramePolygonGeoZones';
import color from '@theme/Colors';
import React from 'react';
import { MdOutlineDelete } from 'react-icons/md';
import { Circle, Image, Layer, Line, Shape, Stage, Text } from 'react-konva';
import { GeoZonePoints, HeadingSection } from '../../AddGeoZones';
import { Mode } from '../../CameraInformation';

export type FramePolygonProps = {
    onChange?(points: GeoZoneCoordinates[]): void;
    onChangeMulti?(data: GeoZonePoints[]): void;
    onChangeCurrent?(data: GeoZonePoints): void;
    onDelete?(points: GeoZoneCoordinates[]): void;
    prevGeoZone?: GeoZoneCoordinates[][];
    selectedGeoZone?: GeoZones;
    imageElement?: HTMLImageElement;
    mode?: Mode;
    geoZoneType?: GeoZoneType;
    style?: React.CSSProperties;
    multiPolygon?: boolean;
};

export default function FramePolygonGeoOverview(props: FramePolygonProps) {
    const {
        stageRef,
        shapeRef,
        points,
        multiPoints,
        isDrag,
        scaledPrevGeoZone,
        scaleSize,
        prevGeoZone,
        selectedGeoZone,
        imageElement,
        containerRef,
        containerSize,
        currentPoints,
        setPoints,
        handleDrawFrame,
        handleDragAnchors,
        handleDragShape,
        handleDeleteLastPolygon,
        handleSelectPolygon,
        setIsDrag,
        updateMultiPointById,
        isPrevCurrentGzInsideFrame,
    } = useFramePolygonGeoZones(props);

    return (
        <>
            {!imageElement ? (
                <Skeleton
                    ref={containerRef}
                    sx={{
                        width: '75%',
                        borderRadius: '4px',
                        bgcolor: color.grey200,
                        minHeight: containerSize.height,
                        margin: '16px auto',
                        ...props.style,
                    }}
                    variant="rectangular"
                />
            ) : (
                <div
                    ref={containerRef}
                    style={{
                        width: '75%',
                        height: containerSize.height,
                        position: 'relative',
                        margin: '56px auto 0px',
                        ...props.style,
                    }}
                >
                    <Stage
                        ref={stageRef}
                        width={containerSize.width}
                        height={containerSize.height}
                        onClick={handleDrawFrame}
                    >
                        <Layer>
                            <Image image={imageElement} scaleX={scaleSize} scaleY={scaleSize} />
                            {!!scaledPrevGeoZone?.length &&
                                scaledPrevGeoZone?.map((frame, frameIndex) => {
                                    const center = getCenterFrame(frame);

                                    const matchingGeoZone = prevGeoZone
                                        ?.flat()
                                        .find((geoZone) => geoZone.GeoZoneId === frame[0].geoZoneId);

                                    const conditionColorLine =
                                        matchingGeoZone?.GeoZone?.GeoZoneType === GeoZoneType.Detail ||
                                        props.geoZoneType === GeoZoneType.Detail;

                                    const strokeColor = conditionColorLine ? color.accentPink : color.primary;

                                    return (
                                        <React.Fragment key={`frame-${frameIndex}`}>
                                            <Line
                                                points={frame.flatMap((point) => [point.x, point.y])}
                                                stroke={strokeColor}
                                                strokeWidth={3}
                                                shadowColor={conditionColorLine ? color.warning : color.gold700}
                                                shadowBlur={20}
                                                fill="rgba(0, 0, 0, 0.12)"
                                                closed={true}
                                            />
                                            {frame[0].geoZoneId !==
                                                selectedGeoZone?.GeoZoneCoordinates?.[0].GeoZoneId && (
                                                <>
                                                    <Circle x={center.x} y={center.y} radius={15} fill={'#FFFFFF'} />
                                                    <Text
                                                        x={center.x}
                                                        y={center.y}
                                                        text={String(`${frameIndex + 1}`)}
                                                        fontSize={14}
                                                        fill={color.textPrimary}
                                                        align={'center'}
                                                        verticalAlign={'middle'}
                                                        offsetX={4}
                                                        offsetY={6}
                                                    />
                                                </>
                                            )}
                                        </React.Fragment>
                                    );
                                })}
                            {props.multiPolygon &&
                                multiPoints.map((polygon, polygonIndex) => (
                                    <Shape
                                        key={`new-polygon-${polygonIndex}`}
                                        sceneFunc={(context, shape) => {
                                            context.beginPath();
                                            const startPoint = polygon.points[0];
                                            context.moveTo(
                                                startPoint.XPoint * scaleSize,
                                                startPoint.YPoint * scaleSize
                                            );
                                            polygon.points.slice(1).forEach((point) => {
                                                context.lineTo(point.XPoint * scaleSize, point.YPoint * scaleSize);
                                            });
                                            context.closePath();
                                            context.strokeShape(shape);
                                            context.fillStrokeShape(shape);
                                        }}
                                        stroke={color.success}
                                        draggable
                                        onDragStart={() => setIsDrag(true)}
                                        onDragEnd={(e) => handleDragShape(e, polygonIndex)}
                                        onClick={() => handleSelectPolygon(polygonIndex)}
                                        onTap={() => handleSelectPolygon(polygonIndex)}
                                        onMouseEnter={() => {
                                            if (stageRef.current) {
                                                stageRef.current.content.style.cursor = 'pointer';
                                            }
                                        }}
                                        onMouseLeave={() => {
                                            if (stageRef.current) {
                                                stageRef.current.content.style.cursor = 'default';
                                            }
                                        }}
                                    />
                                ))}
                            {points.length >= 3 &&
                                points.at(-1)?.XPoint === points[0]?.XPoint &&
                                points.at(-1)?.YPoint === points[0]?.YPoint && (
                                    <Shape
                                        sceneFunc={(context, shape) => {
                                            context.beginPath();
                                            const startPoint = points[0];
                                            context.moveTo(
                                                startPoint.XPoint * scaleSize,
                                                startPoint.YPoint * scaleSize
                                            );
                                            points.slice(1).forEach((point) => {
                                                context.lineTo(point.XPoint * scaleSize, point.YPoint * scaleSize);
                                            });
                                            context.closePath();
                                            context.strokeShape(shape);
                                            context.fillStrokeShape(shape);
                                        }}
                                        stroke={color.success}
                                        ref={shapeRef}
                                        draggable
                                        onDragStart={() => {
                                            setIsDrag(true);
                                        }}
                                        onDragEnd={handleDragShape}
                                        onMouseEnter={() => {
                                            if (stageRef.current) {
                                                const stageElement = stageRef.current.content;
                                                stageElement.style.cursor = 'pointer';
                                            }
                                        }}
                                        onMouseLeave={() => {
                                            if (stageRef.current) {
                                                const stageElement = stageRef.current.content;
                                                stageElement.style.cursor = 'default';
                                            }
                                        }}
                                    />
                                )}

                            {points.map((point, index) => (
                                <React.Fragment key={`anchor-${index}`}>
                                    {index < points.length - 1 && (
                                        <Line
                                            key={`line-${index}`}
                                            points={[
                                                point.XPoint * scaleSize,
                                                point.YPoint * scaleSize,
                                                points[index + 1].XPoint * scaleSize,
                                                points[index + 1].YPoint * scaleSize,
                                            ]}
                                            stroke={isClosedPolygon(points) ? color.success : 'red'}
                                        />
                                    )}
                                    {!isDrag && (
                                        <Circle
                                            key={`circle-${index}`}
                                            x={point.XPoint * scaleSize}
                                            y={point.YPoint * scaleSize}
                                            radius={5}
                                            fill="white"
                                            stroke="black"
                                            strokeWidth={2}
                                            draggable
                                            onDragEnd={(e) => handleDragAnchors(index, e)}
                                            onClick={() => {
                                                if (index === 0 && points.length >= 3 && points.at(-1) !== point) {
                                                    setPoints([...points, point]); // đóng hình
                                                }
                                            }}
                                        />
                                    )}
                                </React.Fragment>
                            ))}

                            {points.map((point, index) => {
                                const anchors: JSX.Element[] = [];

                                const isClosed = isClosedPolygon(points);

                                const nextIndex =
                                    index + 1 < points.length
                                        ? index + 1
                                        : isClosed && index === points.length - 1
                                          ? 0
                                          : -1;

                                const nextPoint = nextIndex >= 0 ? points[nextIndex] : null;

                                // 🔹 Anchor chính
                                if (!isDrag) {
                                    anchors.push(
                                        <Circle
                                            key={`anchor-${index}`}
                                            x={point.XPoint * scaleSize}
                                            y={point.YPoint * scaleSize}
                                            radius={5}
                                            fill="white"
                                            stroke="black"
                                            strokeWidth={2}
                                            draggable
                                            onDragEnd={(e) => handleDragAnchors(index, e)}
                                            onMouseEnter={() => {
                                                stageRef.current!.content.style.cursor = 'pointer';
                                            }}
                                            onMouseLeave={() => {
                                                stageRef.current!.content.style.cursor = 'default';
                                            }}
                                        />
                                    );
                                }

                                // 🔹 Midpoint
                                if (nextPoint && nextIndex !== 0) {
                                    const midX = (point.XPoint + nextPoint.XPoint) / 2;
                                    const midY = (point.YPoint + nextPoint.YPoint) / 2;

                                    anchors.push(
                                        <Circle
                                            key={`midpoint-${index}`}
                                            x={midX * scaleSize}
                                            y={midY * scaleSize}
                                            radius={4}
                                            opacity={0.5}
                                            fill="white"
                                            stroke="black"
                                            strokeWidth={1}
                                            draggable
                                            onDragEnd={(e) => {
                                                const newMidPoint: GeoZoneCoordinates = {
                                                    XPoint: e.target.x() / scaleSize,
                                                    YPoint: e.target.y() / scaleSize,
                                                    Index: index + 2,
                                                    GeoZoneId: selectedGeoZone?.GeoZoneCoordinates?.[0].GeoZoneId,
                                                };

                                                if (isPrevCurrentGzInsideFrame(newMidPoint, currentPoints?.id)) {
                                                    e.target.to({
                                                        x: midX * scaleSize,
                                                        y: midY * scaleSize,
                                                    });
                                                    return;
                                                }

                                                const updatedPoints = [...points];
                                                updatedPoints.splice(index + 1, 0, newMidPoint);

                                                const reIndexed = updatedPoints.map((pt, i) => ({
                                                    ...pt,
                                                    Index: i + 1,
                                                }));

                                                setPoints(reIndexed);
                                            }}
                                            onMouseEnter={() => {
                                                stageRef.current!.content.style.cursor = 'pointer';
                                            }}
                                            onMouseLeave={() => {
                                                stageRef.current!.content.style.cursor = 'default';
                                            }}
                                        />
                                    );
                                }

                                return <React.Fragment key={`group-${index}`}>{anchors}</React.Fragment>;
                            })}
                        </Layer>
                    </Stage>
                    <Stack
                        direction="row"
                        justifyContent="center"
                        sx={{
                            p: 1,
                            position: 'absolute',
                            top: -41,
                            left: 0,
                            right: 0,
                            backgroundColor: '#292929',
                        }}
                        alignItems="center"
                        spacing={1.5}
                    >
                        <BaseIconFrame
                            icon={<MdOutlineDelete fill={color.white} fontSize={17} />}
                            onClick={() => {
                                handleDeleteLastPolygon();
                            }}
                            style={{
                                borderRadius: '50%',
                                border: `1px solid ${color.white}`,
                                cursor: 'pointer',
                                width: 25,
                                height: 25,
                                '&:hover': {
                                    background: 'rgba(255, 255, 255, 0.2)',
                                    opacity: 0.5,
                                    transition: '0.3s ease-in-out',
                                },
                            }}
                        />
                    </Stack>
                </div>
            )}

            {multiPoints.length ? (
                <Stack>
                    <HeadingSection icon={<></>} heading="Zone configuration" />
                    <Stack
                        p={1.7}
                        sx={{
                            border: '1px solid #DDDDDD',
                            borderWidth: '0px 1px 1px 1px',
                            borderRadius: '0px 0px 4px 4px',
                        }}
                        spacing={2}
                    >
                        {multiPoints.map((gz, index) => {
                            const totalBay = gz.totalBay ?? 0;
                            return (
                                <Stack
                                    direction={{ xs: 'column', md: 'row' }}
                                    justifyContent="start"
                                    spacing={{ xs: 1, md: 2 }}
                                    mt={2}
                                    p={{ xs: 1, md: 2 }}
                                    border={`1px solid ${gz.id == currentPoints.id ? color.success : '#DDDDDD'}`}
                                    borderRadius="4px"
                                >
                                    <Typography
                                        sx={{ whiteSpace: 'nowrap', cursor: 'pointer' }}
                                        onClick={() => handleSelectPolygon(index)}
                                    >
                                        Geozone {`${index + (prevGeoZone?.length ?? 0) + 1}`}:
                                    </Typography>
                                    <BaseTextField
                                        label="Name"
                                        isVariantOutlined
                                        value={gz.geoZoneName || ''}
                                        onChange={(e) => {
                                            updateMultiPointById(gz.id, { geoZoneName: e.target.value });
                                        }}
                                        placeholder={'Geozone name'}
                                        maxLength={50}
                                    />
                                    <BaseTextField
                                        label="Total bay"
                                        isVariantOutlined
                                        placeholder={'Total bay'}
                                        value={totalBay}
                                        error={totalBay > 255}
                                        helperText={totalBay > 255 && 'Value must be 255 or less'}
                                        onChange={(e) => {
                                            let val = Number(e.target.value);
                                            const parsed = isNaN(val) ? 0 : val;

                                            updateMultiPointById(gz.id, { totalBay: parsed });
                                        }}
                                    />
                                </Stack>
                            );
                        })}
                    </Stack>
                </Stack>
            ) : (
                <></>
            )}
        </>
    );
}
